/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import '@progress/kendo-theme-default/dist/all.css'
import UserRoleManagement from './user-management/UserRoleManagement'
import PermissionManagement from './permission-management/PermissionManagement'
import RoleManagement from './role-management/RoleManagement'
import {
  useLazyGetRoleUsersQuery,
  useAddUserToRoleMutation,
  useRemoveUserFromRoleMutation,
  useLazyGetRoleExternalUsersQuery,
} from 'app/services/system.service'
import { useAppSelector } from 'app/hooks'
import {
  IRolePermissions,
  SettingsPermissions,
} from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { FixMeLater } from 'types'
import { TITLES } from 'locales/locales'

interface Role {
  _id: string;
  name: string;
}

interface User {
  _id: string
  fullname: string
  avatar: string
}

interface DataState {
  take: number
  skip: number
  filter: {
    logic: 'and' | 'or'
    filters: FixMeLater[] 
  }
  sort?: FixMeLater
}

const AdminRoleSettings = () => {
  const { t } = useTranslation()

  // State management
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [selectedUsers, setSelectedUsers] = useState<FixMeLater>([])
  const [dataState, setDataState] = useState<DataState>({
    take: 10,
    skip: 0,
    filter: {
      logic: 'and',
      filters: [],
    },
  })
  const [roleExternalUsers, setRoleExternalUsers] = useState<FixMeLater>([])

  // API hooks
  const [lazyGetRoleUsers] = useLazyGetRoleUsersQuery()
  const [addUserToRole] = useAddUserToRoleMutation()
  const [removeUserFromRole] = useRemoveUserFromRoleMutation()
  const [lazyGetRoleExternalUsers] = useLazyGetRoleExternalUsersQuery()

  const userPermissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasRoleUpdatePermission = isFlagSetIn(
    [SettingsPermissions.CREATE_UPDATE_ROLES],
    userPermissions.settings,
  )

  // Fetch role details when selected role changes
  useEffect(() => {
    if (selectedRole) {
      fetchRoleUsers()
      fetchRoleExternalUsers()
    }
  }, [selectedRole])

  const fetchRoleUsers = async () => {
    if (!selectedRole) return
    
    try {
      const response = await lazyGetRoleUsers({
        ...dataState,
        count: true,
        id: selectedRole._id,
      })
      setSelectedUsers(response.data.data.map((user: FixMeLater) => ({
        ...user,
        isInRole: true,
      })) || [])
    } catch (error) {
      console.error('Error fetching role users:', error)
    }
  }

  const fetchRoleExternalUsers = async () => {
    if (!selectedRole) return
    
    try {
      const response = await lazyGetRoleExternalUsers({
        ...dataState,
        count: true,
        id: selectedRole._id,
      })
      setRoleExternalUsers(response.data.data.map((user: FixMeLater) => ({
        ...user,
        isInRole: false,
      })) || [])
    } catch (error) {
      console.error('Error fetching role users:', error)
    }
  }

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role)
  }

  const handleUserInRoleChange = (user: User, checked: boolean) => {
    if (!selectedRole) return
    if (checked) {
      addUserToRole({
        _id: selectedRole._id,
        members: [user._id],
      })
      setSelectedUsers([...selectedUsers, {...user, isInRole: true}])
      setRoleExternalUsers(roleExternalUsers.filter((u: FixMeLater) => u._id !== user._id))
    } else {
      removeUserFromRole({
        _id: selectedRole._id,
        members: [user._id],
      })
      setSelectedUsers(selectedUsers.filter((u: FixMeLater) => u._id !== user._id))
      setRoleExternalUsers([...roleExternalUsers, {...user, isInRole: false}])
    }
  }

  const handleBatchUserRoleChange = (users: User[], checked: boolean) => {
    if (!selectedRole || users.length === 0) return
    
    const userIds = users.map(user => user._id)
    
    if (checked) {
      // Tüm kullanıcıları role ekle
      addUserToRole({
        _id: selectedRole._id,
        members: userIds,
      })
      
      // State güncellemeleri
      const newSelectedUsers = users.map(user => ({...user, isInRole: true}))
      setSelectedUsers([...selectedUsers, ...newSelectedUsers])
      setRoleExternalUsers(roleExternalUsers.filter((u: FixMeLater) => !userIds.includes(u._id)))
    } else {
      // Tüm kullanıcıları role'den çıkar
      removeUserFromRole({
        _id: selectedRole._id,
        members: userIds,
      })
      
      // State güncellemeleri
      const newExternalUsers = users.map(user => ({...user, isInRole: false}))
      setSelectedUsers(selectedUsers.filter((u: FixMeLater) => !userIds.includes(u._id)))
      setRoleExternalUsers([...roleExternalUsers, ...newExternalUsers])
    }
  }

  return (
    <ContentCard>
      <ContentCardHeader>{t(TITLES.ROLE_SETTINGS_TITLE)}</ContentCardHeader>
      <ContentCardBody>
        <RoleManagementContainer>
          <ThreeColumnLayout>
            <RoleManagement
              selectedRole={selectedRole}
              onRoleSelect={handleRoleSelect}
            />
            <PermissionManagement selectedRole={selectedRole} />
            <UserRoleManagement
              selectedRole={selectedRole}
              selectedUsers={selectedUsers}
              roleExternalUsers={roleExternalUsers}
              setExternalUsers={setRoleExternalUsers}
              setSelectedUsers={setSelectedUsers}
              onUserInRoleChange={handleUserInRoleChange}
              onBatchUserRoleChange={handleBatchUserRoleChange}
            />
          </ThreeColumnLayout>
        </RoleManagementContainer>
      </ContentCardBody>
    </ContentCard>
  )
}

export default AdminRoleSettings

// Styled Components
const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
  width: 100%;
  height: calc(100vh - 200px);

  @media (max-width: 1024px) {
    height: auto;
    min-height: calc(100vh - 200px);
  }
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 10px;
  height: calc(100% - 60px);

  @media (max-width: 1024px) {
    height: auto;
    padding: 10px 5px;
  }
`

const RoleManagementContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 1024px) {
    height: calc(100vh - 240px);
  }
`

const ThreeColumnLayout = styled.div`
  display: flex;
  height: 100%;
  gap: 24px;

  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
  }
`